/**
 * ConcentrationBarChart — grouped bars: latest concentration per (well × analyte).
 * Native hand-rolled SVG (no Recharts). Nested band scales for the grouping.
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { scaleLinear, scaleBand } from 'd3-scale';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
import { WellSelectorPanel } from './WellSelectorPanel';
import { AnalyteSelectorPanel } from './AnalyteSelectorPanel';
import { WELL_COLORS, ANALYTE_COLORS } from './chartTheme';
import { ChartTooltip } from './ChartTooltip';
import { useRegulatoryLines } from '../hooks/useRegulatoryLines';
import { defaultHiddenWellIds, defaultHiddenAnalytes } from './chartDefaults';

interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

function convertConc(val: number | null, _unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

const MARGIN = { top: 12, right: 24, bottom: 70, left: 64 };
const CHART_HEIGHT = 440;

interface Cell {
  well: string;
  analyteCode: string;
  analyteName: string;
  color: string;
  value: number | null;
}

export function ConcentrationBarChart({ data, unit }: Props) {
  // Grouped bars compare wells × analytes simultaneously — default to all.
  const [hiddenWellIds, setHiddenWellIds] = useState<Set<string>>(new Set());
  const [hiddenAnalytes, setHiddenAnalytes] = useState<Set<string> | null>(null);

  const allAnalytes = useMemo(() => {
    const map = new Map<string, string>();
    for (const well of data.wells) {
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!map.has(code)) map.set(code, analyte.compound);
      }
    }
    return Array.from(map.entries()).map(([code, compound], i) => ({
      code,
      compound,
      color: ANALYTE_COLORS[i % ANALYTE_COLORS.length],
    }));
  }, [data.wells]);

  const effectiveHidden = useMemo(() => {
    if (hiddenAnalytes !== null) return hiddenAnalytes;
    return new Set<string>();
  }, [hiddenAnalytes]);

  const visibleAnalyteCodes = useMemo(
    () => new Set(allAnalytes.filter((a) => !effectiveHidden.has(a.code)).map((a) => a.code)),
    [allAnalytes, effectiveHidden],
  );

  const visibleWells = data.wells.filter((w) => !hiddenWellIds.has(w.well_id));
  const { lines: regulatoryLines } = useRegulatoryLines(data, visibleAnalyteCodes);

  const visibleAnalyteList = useMemo(
    () => allAnalytes.filter((a) => visibleAnalyteCodes.has(a.code)),
    [allAnalytes, visibleAnalyteCodes],
  );

  // Build flat cells: each (well × analyte) pair with latest concentration
  const cells: Cell[] = useMemo(() => {
    const out: Cell[] = [];
    for (const well of visibleWells) {
      for (const a of visibleAnalyteList) {
        const analyte = well.analytes[a.code];
        let value: number | null = null;
        if (analyte && analyte.measurements.length > 0) {
          const sorted = [...analyte.measurements].sort((x, y) => x.date.localeCompare(y.date));
          value = convertConc(sorted[sorted.length - 1].concentration, unit);
        }
        out.push({
          well: well.well_name,
          analyteCode: a.code,
          analyteName: a.compound,
          color: a.color,
          value,
        });
      }
    }
    return out;
  }, [visibleWells, visibleAnalyteList, unit]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, CHART_HEIGHT - MARGIN.top - MARGIN.bottom);

  const wellNames = useMemo(() => Array.from(new Set(cells.map((c) => c.well))), [cells]);

  const xScale = useMemo(
    () =>
      scaleBand<string>()
        .domain(wellNames)
        .range([MARGIN.left, MARGIN.left + innerWidth])
        .padding(0.2),
    [wellNames, innerWidth],
  );

  const xInnerScale = useMemo(
    () =>
      scaleBand<string>()
        .domain(visibleAnalyteList.map((a) => a.code))
        .range([0, xScale.bandwidth()])
        .padding(0.1),
    [visibleAnalyteList, xScale],
  );

  const yDomainMax = useMemo(() => {
    let yMax = 0;
    for (const c of cells) if (c.value !== null && c.value > yMax) yMax = c.value;
    for (const line of regulatoryLines) if (line.value > yMax) yMax = line.value;
    return yMax > 0 ? yMax * 1.1 : 1;
  }, [cells, regulatoryLines]);

  const yScale = useMemo(
    () => scaleLinear().domain([0, yDomainMax]).range([MARGIN.top + innerHeight, MARGIN.top]).nice(),
    [yDomainMax, innerHeight],
  );

  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  const formatY = (v: number) => {
    if (v === 0) return '0';
    const abs = Math.abs(v);
    if (abs < 0.01) return v.toExponential(1);
    if (abs < 1) return v.toPrecision(2);
    return v.toFixed(1);
  };

  interface HoverState { cell: Cell; clientX: number; clientY: number }
  const [hover, setHover] = useState<HoverState | null>(null);

  const handleHover = useCallback(
    (cell: Cell) => (e: React.MouseEvent) => {
      setHover({ cell, clientX: e.clientX, clientY: e.clientY });
    },
    [],
  );
  const handleLeave = useCallback(() => setHover(null), []);

  if (!data.wells.length) {
    return (
      <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
        NO ANALYTICAL DATA AVAILABLE
      </div>
    );
  }

  const wellItems = data.wells.map((w, i) => ({
    well_id: w.well_id,
    well_name: w.well_name,
    color: WELL_COLORS[i % WELL_COLORS.length],
  }));
  const visibleWellIdSet = new Set(visibleWells.map((w) => w.well_id));

  return (
    <div className="space-y-3">
      <p className="text-xs mb-3" style={{ color: 'var(--dv-text-muted)' }}>
        Latest concentration by well, grouped by analyte. Bars within each well-group represent
        different analytes' most recent sample values.
      </p>

      <WellSelectorPanel
        wells={wellItems}
        visibleWellIds={visibleWellIdSet}
        onToggle={(id) => {
          setHiddenWellIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          });
        }}
        onSelectAll={() => setHiddenWellIds(new Set())}
        onDeselectAll={() => setHiddenWellIds(new Set(data.wells.map((w) => w.well_id)))}
      />

      <AnalyteSelectorPanel
        analytes={allAnalytes}
        visibleCodes={visibleAnalyteCodes}
        onToggle={(code) => {
          const next = new Set(effectiveHidden);
          if (next.has(code)) next.delete(code);
          else next.add(code);
          setHiddenAnalytes(next);
        }}
        onSelectAll={() => setHiddenAnalytes(new Set())}
        onDeselectAll={() => setHiddenAnalytes(new Set(allAnalytes.map((a) => a.code)))}
      />

      <div ref={containerRef}>
        {cells.length === 0 || visibleAnalyteList.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
            SELECT WELLS AND ANALYTES TO DISPLAY
          </div>
        ) : (
          <svg
            width={width || '100%'}
            height={CHART_HEIGHT}
            style={{ display: 'block', userSelect: 'none' }}
          >
            {/* Gridlines */}
            {yTicks.map((t) => {
              const y = yScale(t);
              return (
                <line
                  key={`grid-${t}`}
                  x1={MARGIN.left}
                  x2={MARGIN.left + innerWidth}
                  y1={y}
                  y2={y}
                  stroke="var(--dv-chart-grid)"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
              );
            })}

            {/* Regulatory lines */}
            {regulatoryLines.map((line) => {
              const v = convertConc(line.value, unit);
              if (v === null || v > yDomainMax) return null;
              const y = yScale(v);
              const dashArray = line.style === 'dashed' ? '8 4' : line.style === 'dotted' ? '2 4' : undefined;
              return (
                <g key={line.key} style={{ filter: 'url(#dv-mcl-glow)' }}>
                  <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={y} y2={y} stroke={line.color} strokeWidth={1} strokeDasharray={dashArray} />
                  <text x={MARGIN.left + innerWidth - 4} y={y - 4} textAnchor="end" fontSize={9} fill={line.color}>
                    {`${line.label} ${line.analyteName}`}
                  </text>
                </g>
              );
            })}

            {/* Bars */}
            {cells.map((c, i) => {
              if (c.value === null) return null;
              const xWell = xScale(c.well) ?? 0;
              const xInner = xInnerScale(c.analyteCode) ?? 0;
              const x = xWell + xInner;
              const y = yScale(c.value);
              const yBase = yScale(0);
              const isHovered = hover?.cell === c;
              return (
                <rect
                  key={`bar-${i}`}
                  x={x}
                  y={y}
                  width={Math.max(0, xInnerScale.bandwidth())}
                  height={Math.max(0, yBase - y)}
                  fill={c.color}
                  opacity={isHovered ? 1 : 0.82}
                  rx={2}
                  onMouseEnter={handleHover(c)}
                  onMouseMove={handleHover(c)}
                  onMouseLeave={handleLeave}
                  style={{ cursor: 'crosshair' }}
                />
              );
            })}

            {/* Y-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {yTicks.map((t) => {
              const y = yScale(t);
              return (
                <g key={`yt-${t}`}>
                  <line x1={MARGIN.left - 4} x2={MARGIN.left} y1={y} y2={y} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={MARGIN.left - 8} y={y} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatY(t)}
                  </text>
                </g>
              );
            })}
            <text transform={`translate(${MARGIN.left - 48}, ${MARGIN.top + innerHeight / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
              Concentration (mg/L)
            </text>

            {/* X-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {wellNames.map((w) => {
              const x = (xScale(w) ?? 0) + xScale.bandwidth() / 2;
              return (
                <text
                  key={`xt-${w}`}
                  transform={`translate(${x}, ${MARGIN.top + innerHeight + 12}) rotate(-35)`}
                  textAnchor="end"
                  fontSize={11}
                  fill="var(--dv-chart-axis)"
                >
                  {w}
                </text>
              );
            })}
          </svg>
        )}
      </div>

      {/* Legend */}
      {visibleAnalyteList.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap text-[10px]" style={{ color: 'var(--dv-text-muted)' }}>
          {visibleAnalyteList.map((a) => (
            <span key={a.code} className="inline-flex items-center gap-1.5">
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: a.color }} />
              {a.compound}
            </span>
          ))}
        </div>
      )}

      {hover && hover.cell.value !== null && (
        <ChartTooltip visible x={hover.clientX} y={hover.clientY} placement="top">
          <div style={{ minWidth: 160, fontSize: 11 }}>
            <div style={{ fontSize: 10, color: 'var(--dv-tooltip-label, rgba(255,255,255,0.7))', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              {hover.cell.well}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 999, background: hover.cell.color }} />
              <span style={{ flex: 1 }}>{hover.cell.analyteName}</span>
              <span style={{ fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)', fontWeight: 600 }}>
                {hover.cell.value.toFixed(3)} mg/L
              </span>
            </div>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}
